import {DecodingError, getErrorPaths} from '@raison/io-ts-tools';
import {identity, isPlainObject} from '@raison/toolbox';
import * as Sentry from '@sentry/react';
import {StandardSchemaV1} from '@standard-schema/spec';
import {isLeft} from 'fp-ts/lib/Either';
import {Decoder, Encoder} from 'io-ts';
import {atomWithStorage} from 'jotai/utils';
import {useEffect, useState} from 'react';

// FIXME: FSD violation, move to shared or to library
import {SemVer} from '../../entities/versions';

export namespace ManagedStorage {
    export interface CodecI<T> {
        encode: (value: T) => string;
        decode: (string: string) => T;
    }

    export interface AccessorI<T> {
        set: (val: T) => void;
        get: () => T | null;
        remove: () => void;
    }

    export interface DriverI {
        getItem: (key: string) => string | null;
        setItem: (key: string, value: string) => void;
        removeItem: (key: string) => void;
    }

    export const defaultCodecs = getDefaultCodecs();
}

const BuildUtils = {
    entry: createStorageEntry,
    migrate: migrateStorageEntry,
    spread: spreadStorageEntries,
    codecs: ManagedStorage.defaultCodecs,
};

let BuildContext: {driver: Driver; version: SemVer} | undefined;

/**
 * Creates type-safe, chainable wrapper around Storage.
 *
 * ### The goals of this approach:
 * - to make it clear what storage keys are in use by application
 * - to make access to storages type-safe
 * - to allow safely renaming variables by which we access storage
 *   without accidentally renaming storage keys (thus creating a breaking change without migration)
 * - to make migrations non-destructive, allowing release rollbacks (intentional or accidental)
 *
 * ### Examples
 * Form basic examples see test cases under "Normal usage"
 */
export function buildManagedStorage<T extends ManagedStorageEntries>(
    version: string | SemVer,
    driver: Driver | ManagedStorage,
    build: (builder: typeof BuildUtils) => T
): ManagedStorage<T> & T {
    if (typeof version == 'string') {
        const decoded = SemVer.fromString.decode(version);
        if (isLeft(decoded)) {
            fail(`Provided invalid version "${version}"`);
        }
        version = decoded.right;
    }

    let parent: ManagedStorageMeta | null = null;
    if (storageMeta in driver) {
        parent = driver[storageMeta];
        driver = parent.driver;
    }

    let entries: T;
    try {
        BuildContext = {driver, version};
        entries = build(BuildUtils);
    } finally {
        BuildContext = undefined;
    }

    sanity: if (import.meta.env.DEV) {
        for (const [variableKey, entry] of Object.entries(entries)) {
            if (entry[storageEntryMeta].driver !== driver) {
                fail(`Mismatching driver found at ${variableKey}:"${entry[storageEntryMeta].key}"`);
            }
        }

        if (!parent) {
            break sanity;
        }

        const diff = SemVer.diff(parent.version, version);
        if (!diff) {
            fail('Cannot base new storage on previous version');
        }
        if (diff.action === 'rollback') {
            fail('Cannot base new storage schema on older version');
        }

        const allEntries = new Map<string, StorageAccessor<any>>(
            Object.values(entries).map(entry => [entry[storageEntryMeta].key, entry])
        );
        let ancestor: typeof parent | null = parent;
        while (ancestor) {
            for (const entry of Object.values(ancestor.entries)) {
                const key = entry[storageEntryMeta].key;
                const repeatedEntry = allEntries.get(key);
                if (!repeatedEntry) {
                    allEntries.set(key, entry);
                    continue;
                }
                if (repeatedEntry !== entry) {
                    fail(`Cannot declare entry "${key}", it was used in ${SemVer.format(ancestor.version)}`);
                }
            }
            ancestor = ancestor.parent;
        }
    }

    const meta: ManagedStorageMeta<T> = {
        version,
        parent,
        driver,
        entries,
    };

    return {
        ...entries,
        [storageMeta]: meta,
    };
}

class ManagedStorageError extends Error {
    readonly name = 'ManagedStorageError';
}
function fail(message: string, cause?: Error): never {
    const error = new ManagedStorageError(message, {cause});
    const stack = error.stack!.split('\n');
    stack.splice(1, 1);
    error.stack = stack.join('\n');
    throw error;
}

const storageEntryMeta = Symbol();
const storageMeta = Symbol();

interface StorageAccessorMeta {
    key: string;
    driver: Driver;
}
interface StorageAccessor<T> extends Accessor<T> {
    [storageEntryMeta]: StorageAccessorMeta;
}

type ManagedStorageEntries = Record<string, StorageAccessor<any>>;

interface ManagedStorage<T extends ManagedStorageEntries = ManagedStorageEntries> {
    [storageMeta]: ManagedStorageMeta<T>;
}
interface ManagedStorageMeta<T extends ManagedStorageEntries = ManagedStorageEntries> {
    parent: null | ManagedStorageMeta;
    driver: Driver;
    version: SemVer;
    entries: T;
}

type Codec<T> = ManagedStorage.CodecI<T>;
type Accessor<T> = ManagedStorage.AccessorI<T>;
type Driver = ManagedStorage.DriverI;

const IdentityCodec = {
    decode: identity,
    encode: identity,
};

function runDecode<T>(codec: Codec<T>, value: string, accessor: Accessor<T>) {
    try {
        return codec.decode(value);
    } catch (error) {
        accessor.remove();
        reportError(error);
        return null;
    }
}

function runSetItem<T>(key: string, update: T, codec: Codec<T>, driver: Driver) {
    const valueToStore = codec.encode(update);
    try {
        codec.decode(valueToStore);
    } catch (error) {
        reportError(error);
        return;
    }
    driver.setItem(key, valueToStore);
}

function getDefaultCodecs() {
    const booleanCodec: Codec<boolean> = {
        encode(value) {
            return String(value);
        },
        decode(string) {
            const result = JSON.parse(string);
            if (typeof result !== 'boolean') {
                throw new Error(`Expected boolean, got ${typeof result}`);
            }
            return result;
        },
    };

    const numberCodec: Codec<number> = {
        encode(value) {
            return String(value);
        },
        decode(string) {
            const result = JSON.parse(string);
            if (typeof result !== 'number') {
                throw new Error(`Expected number, got ${typeof result}`);
            }
            return result;
        },
    };

    const createIoTsCodec = <T>(ioTsCodec: Encoder<T, string> & Decoder<string, T>): Codec<T> => ({
        encode: value => ioTsCodec.encode(value),
        decode: string => {
            const decoded = ioTsCodec.decode(string);
            if (isLeft(decoded)) {
                throw DecodingError.from(new Error(getErrorPaths(decoded.left)));
            }
            return decoded.right;
        },
    });

    const createStandardSchemaCodec = <T>(schema: StandardSchemaV1<T>): Codec<T> => ({
        encode: value => JSON.stringify(value),
        decode: string => {
            const result = schema['~standard'].validate(string);
            if (result instanceof Promise) {
                throw new Error('Manged storage cannot use async decoding');
            }
            if (result.issues) {
                const report = result.issues.map(i => i.message).join('\n');
                throw new Error(`Managed storage decoding failed:\n${report}`);
            }
            return result.value;
        },
    });

    const createLazyCodec = <T>(getCodec: () => Codec<T>): Codec<T> => ({
        decode: value => getCodec().decode(value),
        encode: string => getCodec().encode(string),
    });

    return {
        boolean: booleanCodec,
        number: numberCodec,
        io: createIoTsCodec,
        json: createStandardSchemaCodec,
        lazy: createLazyCodec,
    };
}

type MigrationConfig<TFrom, TTo> = {
    from: Accessor<TFrom> | {[K in keyof TFrom]: Accessor<TFrom[K]>};
    to: (oldValue: TFrom) => TTo;
};

function migrateStorageEntry<TOld>(key: string, config: MigrationConfig<TOld, string>): StorageAccessor<string>;
function migrateStorageEntry<TOld, T>(
    key: string,
    config: MigrationConfig<TOld, T> & {codec: Codec<T>}
): StorageAccessor<T>;
function migrateStorageEntry<TOld, T>(
    key: string,
    {codec: _codec, from, to: migrate}: MigrationConfig<TOld, T> & {codec?: Codec<T>}
): StorageAccessor<T> {
    const {driver} = BuildContext!;

    const codec: Codec<T> = _codec || IdentityCodec;

    if (!('get' in from && 'remove' in from)) {
        from = combineAccessors(from);
    }
    const base = from;

    function getIt(): T | null {
        const rawValue = driver.getItem(key);
        if (rawValue === null) {
            const oldValue = base.get();
            if (oldValue === null) {
                return null;
            }
            return migrate(oldValue);
        }
        return runDecode(codec, rawValue, accessor);
    }

    const accessor: StorageAccessor<T> = {
        [storageEntryMeta]: {
            key,
            driver,
        },
        get: getIt,
        remove() {
            driver.removeItem(key);
            base.remove();
        },
        set(update) {
            runSetItem(key, update, codec, driver);
        },
    };
    return accessor;
}

function combineAccessors<T>(collection: {[K in keyof T]: Accessor<T[K]>}) {
    const {isArray} = Array;
    if (!(isPlainObject(collection) || isArray(collection))) {
        throw new ManagedStorageError('Cannot combine non-array or non-record collection of storage entries');
    }

    const getClone = (isArray(collection) ? () => [] : () => ({})) as () => T;

    const accessor: Accessor<T> = {
        get() {
            const clone = getClone();
            for (const key in collection) {
                const accessor = collection[key];
                const value = accessor.get();
                if (!value) {
                    return null;
                }
                clone[key] = value;
            }
            return clone;
        },
        set(update) {
            for (const key in update) {
                const value = update[key];
                const accessor = collection[key];
                accessor.set(value);
            }
        },
        remove() {
            for (const key in collection) {
                const accessor = collection[key];
                accessor.remove();
            }
        },
    };

    return accessor;
}

function createStorageEntry(key: string): StorageAccessor<string>;
function createStorageEntry<T>(key: string, codec: Codec<T>): StorageAccessor<T>;
function createStorageEntry<T>(key: string, _codec?: Codec<T>): StorageAccessor<T> | StorageAccessor<string> {
    const {driver} = BuildContext!;

    const codec: Codec<T> = _codec || IdentityCodec;

    const accessor: StorageAccessor<T> = {
        [storageEntryMeta]: {
            key,
            driver,
        },
        get() {
            const rawValue = driver.getItem(key);
            if (rawValue === null) {
                return null;
            }
            return runDecode(codec, rawValue, accessor);
        },
        set(update) {
            runSetItem(key, update, codec, driver);
        },
        remove() {
            driver.removeItem(key);
        },
    };
    return accessor;
}

function spreadStorageEntries<T extends ManagedStorageEntries>(storage: ManagedStorage<T>): T {
    return storage[storageMeta].entries;
}

export function clearManagedStorage(storage: ManagedStorage) {
    const {driver, entries} = storage[storageMeta];
    for (const entry of Object.values(entries)) {
        if (entry[storageEntryMeta].driver === driver) {
            entry.remove();
        }
    }
}

export function useStorageAccessor<T>(accessor: StorageAccessor<T>) {
    const [value, setValue] = useState(() => accessor.get());
    const setState = (value: T) => {
        accessor.set(value);
        setValue(value);
    };

    useEffect(() => {
        const handler = (ev: StorageEvent): void => {
            const meta = accessor[storageEntryMeta];
            if (ev.key === meta.key || ev.storageArea === meta.driver) {
                setValue(accessor.get());
            }
        };
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener('storage', handler);
        };
    }, [accessor]);

    return [value, setState] as const;
}

export function atomWithStorageAccessor<T>(accessor: StorageAccessor<T>) {
    return atomWithStorage(
        '_',
        null,
        {
            getItem: _key => accessor.get(),
            setItem: (_key, value) => {
                if (value !== null) {
                    accessor.set(value);
                } else {
                    accessor.remove();
                }
            },
            removeItem: () => accessor.remove(),
            subscribe: (_key, callback) => {
                const handler = (ev: StorageEvent): void => {
                    const meta = accessor[storageEntryMeta];
                    if (ev.key === meta.key || ev.storageArea === meta.driver) {
                        callback(accessor.get());
                    }
                };
                window.addEventListener('storage', handler);
                return () => {
                    window.removeEventListener('storage', handler);
                };
            },
        },
        {
            getOnInit: true,
        }
    );
}

/**
 * Designed to put a crutch on a problem with sessionStorage in iOS app where it
 * appears as if sessionStorage has been cleared after a period of inactivity, but
 * application memory was saved or restored to allow app interactivity.
 */
export function createSessionStorageInMemoryDuplicationDriver({sessionStorage: disk}: {sessionStorage: Storage}) {
    // Intentionally not initializing heap with values from session storage
    // because we can't know keys that will be stored.
    const heap = new Map<string, string>();

    let scheduledCheckId: number | undefined;

    const wrapper: Driver = {
        getItem(key) {
            const onHeapValue = heap.get(key) ?? null;
            const onDiskValue = disk.getItem(key);
            checkSynchronization();
            return onDiskValue ?? onHeapValue;
        },
        setItem(key, value) {
            heap.set(key, value);
            disk.setItem(key, value);
            checkSynchronization();
        },
        removeItem(key) {
            heap.delete(key);
            disk.removeItem(key);
        },
    };
    return wrapper;

    function checkSynchronization() {
        if (scheduledCheckId !== undefined) {
            return;
        }
        scheduledCheckId = window.setTimeout(() => {
            window.clearTimeout(scheduledCheckId);
            scheduledCheckId = undefined;

            if (heap.size > disk.length) {
                reportAnomaly();
                for (const [key, value] of heap) {
                    disk.setItem(key, value);
                }
            }
        });
    }

    function reportAnomaly() {
        const message = 'Anomalous sessionStorage behavior';
        const extra = {
            diskLength: disk.length,
            keysOnDisk: Object.keys(disk),
            heapLength: heap.size,
            keysOnHeap: [...heap.keys()],
        };
        if (import.meta.env.DEV) {
            console.debug(`%c${message}`, 'color:orange;', extra);
        } else {
            Sentry.captureMessage(message, {extra});
        }
    }
}
